import {
  Container,
  Grid,
  IconButton,
  Menu,
  Typography
} from '@mui/material'
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowModel,
  GridValueGetterParams
} from '@mui/x-data-grid'
import {
  ConfirmDialog,
  DataGridStyles,
  Icon,
  LinearProgressBar,
  SnackBarContext
} from 'components'
import { DatagridPlaceholder } from 'components/datagridPlaceholder'
import { theme } from 'config'
import { runInAction } from 'mobx'
import { inject, observer } from 'mobx-react'
import { Course, User } from 'models'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { RoutingParams } from 'routes'
import { AuthStore, RoutingStore } from 'stores'
import styled from 'styled-components'


export interface IUserCourseListProps {
  authStore?:    AuthStore,
  routingStore?: RoutingStore,
}

export const UserCourseList = inject('routingStore')(observer((props: IUserCourseListProps) => {
  const { t } = useTranslation();
  const params: RoutingParams = useParams<RoutingParams>();
  const { toast } = React.useContext(SnackBarContext);

  const [course,   setCourse]   = useState<Course>();
  const [user,     setUser]     = React.useState<User>();
  const [openConfirm, setOpenConfirm] = useState(false);
  const [loading, setLoading] = useState(false);


  useEffect(() => {
    User.get(Number(params.subid)).then((u: User) => {
      setUser(u);
      u.loadCourses();
    })
  }, []);

  const renderActionButton = (params: GridRenderCellParams<number,RowData>) => {
    return (
      <IconButton
        onClick={() => { 
          setOpenConfirm(true)
          setCourse(params.row.course)
        }}
        >
        <Icon iconId='retake'/>
      </IconButton>
    );
  }

  const columns: GridColDef[] = [
    {
      field:           'headerName',
      headerName:      t('name'),
      headerClassName: 'columnHeader',
      flex:            2,
      minWidth:        60,
      valueGetter:     (params: GridValueGetterParams) =>
        `${params.row.headerName || ''}`,
    },{
      field:           'category',
      headerName:      t('category'),
      flex:            1,
      headerClassName: 'columnHeader',
    },{
      field:           'progress',
      headerName:      t('progress'),
      flex:            1,
      renderCell:      (params: GridRenderCellParams<number, RowData>) => (
        <LinearProgressBar label={`${Math.round(params.row.percentage) || 0}%`} value={Math.round(params.row.percentage) || 0} />
      ),
      headerClassName: 'columnHeader',
    },{
      field:           'actions',
      type:            'actions',
      headerName:      t('reset_progress'),
      sortable:        false,
      flex:            1,
      renderCell:      renderActionButton,
      align:           'right',
      headerAlign:     'right',
      headerClassName: 'actionHeader',
    }
  ];

  type RowData = {
    id:         number,
    course:     Course,
    headerName: string,
    category:   string,
    percentage: number,
  }


  let rows: GridRowModel<RowData>[] = [];
  rows = user?.courses?.map((c) => ({
    id:         c.id,
    course:     c,
    category:   c.category,
    headerName: c.title,
    percentage: c.progress,
  }));


  return (
    <Container maxWidth='xl'>
      <Grid pt={3} container>
        <Grid
          item
          xs             = {12}
          display        = 'flex'
          justifyContent = 'space-between'
          alignItems     = 'center'
          mb             = {3}
        >
          <Typography
            variant    = 'h2'
            fontWeight = {theme.typography.fontWeightBold}
          >
            {t('courses')}
          </Typography>
        </Grid>
      </Grid>
      <DataGridStyles>
          <DataGrid
            autoHeight
            disableSelectionOnClick
            disableColumnSelector
            components = {{
              NoRowsOverlay: () =>
              (<DatagridPlaceholder title={t('datagrid_placeholder_course_user_list')}/>)
            }}
            initialState={{
              sorting: {
                sortModel: [{ field: 'headerName', sort: 'asc' }],
              },
            }}
            rowHeight          = {66}
            density            = 'standard'
            rows               = {rows || []}
            columns            = {columns}
            pageSize           = {10}
            rowsPerPageOptions = {[10]}
          />
      </DataGridStyles>
        <ConfirmDialog 
          open={openConfirm}
          title             = {t('confirm_reset_progress_title') + '?'}
          onCancel          = {() => setOpenConfirm(false)}
          onClose           = {() => setOpenConfirm(false)}
          dialogContent     = {t('confirm_reset_progress_body')}
          confirmButtonText = {t('yes')}
          disabled          = {loading}
          loading           = {loading}
          cancelButtonText  = {t('cancel')}
          onConfirm         = {async () => {
            setLoading(true);
            if (await user.resetProgress(course.id)) {
              toast(t('toast_course_progress_reset'), 'success');
              runInAction(() => course.progress = 0);
              setOpenConfirm(false);
            } else {
              toast(t('course_progress_reset_fail'), 'error');
            } 
            setLoading(false);
          }}
        />
    </Container> 
  );
}));

const PopUpMenu = styled(Menu)`
  .MuiList-root {
    padding-top:    0;
    padding-bottom: 0;
  }

  .MuiMenuItem-root {
    :not(:last-child) {
      border-bottom: 2px solid #f4f4f0;
    }
    padding: 0.5rem 1rem;
  }
`;
